// src/views/WeeklyPlannerView.js
// ─────────────────────────────────────────────────────────────────────────────
// Weekly Territory Planner — Multi-Crew Edition
// Step 1 — Configure: date range, crew multi-select, customers, durations
// Step 2 — Preview:   day × crew grid, drag across both dimensions
// Step 3 — Confirm:   per-crew summary, booking emails, batch job creation
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, functions } from '../firebase/config';
import { collection, query, onSnapshot, orderBy, writeBatch, doc, serverTimestamp, getDocs, where, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { can, PERMISSIONS } from '../utils/permissions';
import { buildAddressString } from '../utils/address';
import * as lineIds from '../utils/lineIds'; // lineidweb-9e4a2d17 — permanent per-line IDs + the one template function
import { getArrivalWindow } from '../utils/arrivalWindows'; // aw-wpv-8192a3b4
import {
    CalendarDays, Users, Zap, ChevronRight, ChevronLeft, CheckCircle,
    AlertTriangle, MapPin, Clock, RotateCcw, GripVertical, Info, ChevronUp, ChevronDown, ArrowRightLeft, History, Repeat, RefreshCw, CalendarCheck, FolderOpen, Trash2,
    Mail, MailX, Loader2, Search, X, Lock, FileText
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { withCustomerName, compareCustomersByName } from '../utils/customerDisplay'; // webcustname-7c4a1e92

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const CREW_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1',
    '#f97316', '#84cc16',
];

// A1: a "multi-crew job" is one whose work is split across more than one crew,
// tracked on the job document via crews[] / crewsTotal (built from per-line-item
// crew assignments in the job form). The weekly grid is single-crew per stop and
// must never flatten or drag these — it only surfaces their existence (banner).
// NOTE: this reads the JOB's crews[] — NOT the grid's day.crews structure, which
// is an unrelated same-named model ({ crewId: { customers, stats } }).
const isMultiCrewJob = (job) =>
    (job?.crewsTotal || 0) > 1 ||
    (Array.isArray(job?.crews) && job.crews.length > 1);

const toDateInput = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toISOString().split('T')[0];
};

const formatMins = (mins) => {
    if (!mins && mins !== 0) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const getWorkingDays = (startStr, endStr) => {
    if (!startStr || !endStr) return [];
    const start = new Date(startStr + 'T00:00:00');
    const end   = new Date(endStr   + 'T00:00:00');
    const days  = [];
    const cur   = new Date(start);
    while (cur <= end && days.length < 14) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) days.push(toDateInput(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return days;
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────────────────────────────────────
const StepIndicator = ({ step }) => (
    <div className="flex items-center gap-2 mb-6">
        {['Configure', 'Preview', 'Confirm'].map((label, i) => {
            const num = i + 1;
            const active = step === num;
            const done   = step > num;
            return (
                <React.Fragment key={num}>
                    <div className="flex items-center gap-1.5">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                            done ? 'bg-green-500 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                            {done ? <CheckCircle size={14} /> : num}
                        </div>
                        <span className={`text-sm font-medium hidden sm:block ${active ? 'text-blue-600' : done ? 'text-green-600' : 'text-gray-400'}`}>
                            {label}
                        </span>
                    </div>
                    {i < 2 && <div className={`flex-1 h-0.5 transition-colors ${step > num ? 'bg-green-400' : 'bg-gray-200'}`} />}
                </React.Fragment>
            );
        })}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// CREW-DAY CELL
// ─────────────────────────────────────────────────────────────────────────────
const CrewDayCell = ({ crewData, crewColor, crewName, onDragStart, onDrop, onDragOver, onDragLeave, isDragOver, onMoveCustomer, onOpenMoveSheet, isMobile }) => {
    const customers = crewData?.customers || [];
    const stats     = crewData?.stats || {};
    const pct       = Math.min(100, ((stats.totalMinutes || 0) / 480) * 100);

    return (
        <div
            className={`bg-white rounded-lg border transition-all ${isDragOver ? 'border-blue-400 shadow-md ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200'}`}
            onDragOver={e => { if (!isMobile) { e.preventDefault(); onDragOver(); } }}
            onDragLeave={() => { if (!isMobile) onDragLeave(); }}
            onDrop={e => { if (!isMobile) { e.preventDefault(); onDrop(); } }}
        >
            {/* Mini header */}
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b" style={{ borderLeftWidth: 3, borderLeftColor: crewColor }}>
                <span className="text-xs font-semibold text-gray-700 truncate">{crewName}</span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] text-gray-400">{customers.length} stops</span>
                    {stats.isOverCapacity && <AlertTriangle size={10} className="text-red-500" />}
                </div>
            </div>

            {/* Capacity bar */}
            <div className="px-2.5 pt-1.5 pb-1">
                <div className="w-full bg-gray-100 rounded-full h-1">
                    <div className="h-1 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: stats.isOverCapacity ? '#ef4444' : crewColor }} />
                </div>
                <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                    <span>{formatMins(stats.totalServiceMinutes)} svc</span>
                    <span>{formatMins(stats.driveMinutes)} drive</span>
                </div>
            </div>

            {/* Customer list */}
            <div className="px-2 pb-2 space-y-1 max-h-40 overflow-y-auto">
                {customers.length === 0 ? (
                    <p className="text-[10px] text-gray-300 italic text-center py-2">{isDragOver && !isMobile ? 'Drop here' : 'Empty'}</p>
                ) : customers.map((c, idx) => (
                    <div
                        key={c.id}
                        draggable={!isMobile}
                        onDragStart={() => { if (!isMobile) onDragStart(c); }}
                        className="flex items-center gap-1.5 p-1.5 rounded bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-200 transition-colors group"
                    >
                        <div className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                             style={{ backgroundColor: crewColor }}>
                            {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-gray-800 truncate">{c.name}</p>
                        </div>
                        {c.recurringJobId && <Repeat size={9} className="text-green-500 flex-shrink-0" title="Recurring job" />}
                        {c.preferredCrewId && <Lock size={9} className="text-blue-400 flex-shrink-0" title="Crew locked" />}
                        <span className="text-[9px] text-gray-400 flex-shrink-0">{formatMins(c.estimatedJobDuration)}</span>

                        {isMobile ? (
                            /* Mobile: reorder within cell + Move button for cross-cell */
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <div className="flex flex-col gap-0.5">
                                    <button type="button" disabled={idx === 0}
                                        onClick={e => { e.stopPropagation(); onMoveCustomer && onMoveCustomer(c, idx, idx - 1); }}
                                        className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-20">
                                        <ChevronUp size={10} className="text-gray-500" />
                                    </button>
                                    <button type="button" disabled={idx === customers.length - 1}
                                        onClick={e => { e.stopPropagation(); onMoveCustomer && onMoveCustomer(c, idx, idx + 1); }}
                                        className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-20">
                                        <ChevronDown size={10} className="text-gray-500" />
                                    </button>
                                </div>
                                <button type="button"
                                    onClick={e => { e.stopPropagation(); onOpenMoveSheet && onOpenMoveSheet(c); }}
                                    className="p-1 rounded-md bg-blue-50 hover:bg-blue-100 transition-colors"
                                    aria-label="Move to another day or crew">
                                    <ArrowRightLeft size={11} className="text-blue-600" />
                                </button>
                            </div>
                        ) : (
                            /* Desktop: grip handle — drag is active */
                            <GripVertical size={10} className="text-gray-300 group-hover:text-gray-400 flex-shrink-0 cursor-grab" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// ─── TemplateCombobox ─────────────────────────────────────────────────────────
// Reusable searchable template picker. Type to filter, click to select, X to clear.
// ─────────────────────────────────────────────────────────────────────────────
const TemplateCombobox = ({ templates, value, onChange, placeholder = 'Search templates…', size = 'md' }) => {
    const [query, setQuery] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);

    const selected = templates.find(t => t.id === value) || null;

    const filtered = query.trim()
        ? templates.filter(t => t.name.toLowerCase().includes(query.toLowerCase()))
        : templates;

    // Close on outside click
    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const inputClass = size === 'sm'
        ? 'w-full text-xs px-2 py-1.5 border rounded-md outline-none focus:ring-1 focus:ring-blue-500 bg-white'
        : 'w-full text-sm px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white';

    return (
        <div ref={ref} className="relative">
            {selected && !open ? (
                <div className={`flex items-center gap-1.5 ${size === 'sm' ? 'px-2 py-1.5' : 'px-3 py-2'} border rounded-${size === 'sm' ? 'md' : 'lg'} bg-blue-50 border-blue-200 cursor-pointer`}
                    onClick={() => { setQuery(''); setOpen(true); }}>
                    <span className={`flex-1 truncate font-medium text-blue-800 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>{selected.name}</span>
                    {selected.estimatedDuration && (
                        <span className={`text-blue-500 flex-shrink-0 ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`}>{selected.estimatedDuration}m</span>
                    )}
                    <button type="button" onClick={e => { e.stopPropagation(); onChange(''); }}
                        className="text-blue-400 hover:text-blue-700 flex-shrink-0">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </button>
                </div>
            ) : (
                <input
                    type="text"
                    autoFocus={open}
                    className={inputClass}
                    placeholder={open ? 'Type to search…' : placeholder}
                    value={open ? query : ''}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                />
            )}
            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    <button type="button"
                        onClick={() => { onChange(''); setQuery(''); setOpen(false); }}
                        className={`w-full text-left px-3 py-2 ${size === 'sm' ? 'text-xs' : 'text-sm'} text-gray-400 hover:bg-gray-50 border-b`}>
                        No template
                    </button>
                    {filtered.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-gray-400 italic">No templates match "{query}"</p>
                    ) : filtered.map(t => (
                        <button key={t.id} type="button"
                            onClick={() => { onChange(t.id); setQuery(''); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${value === t.id ? 'bg-blue-50' : ''}`}>
                            <p className={`font-medium text-gray-800 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>{t.name}</p>
                            {(t.estimatedDuration || t.lineItems?.length) && (
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                    {t.estimatedDuration ? `${t.estimatedDuration} min` : ''}
                                    {t.estimatedDuration && t.lineItems?.length ? ' · ' : ''}
                                    {t.lineItems?.length ? `${t.lineItems.length} line item${t.lineItems.length !== 1 ? 's' : ''}` : ''}
                                </p>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};


const WeeklyPlannerView = ({ currentUserProfile, employees }) => {
    const [step, setStep] = useState(1);
    const orgId           = currentUserProfile?.organizationId;

    // ── Mobile detection ──────────────────────────────────────────────────
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // ── Move action sheet (mobile cross-cell moves) ───────────────────────
    const [moveSheet, setMoveSheet] = useState(null);

    // ── Step 1 ────────────────────────────────────────────────────
    const [startDate, setStartDate] = useState(() => {
        const t = new Date(); const m = new Date(t);
        m.setDate(t.getDate() + ((1 + 7 - t.getDay()) % 7 || 7));
        return toDateInput(m);
    });
    const [endDate, setEndDate] = useState(() => {
        const t = new Date();
        const nextMon = new Date(t);
        nextMon.setDate(t.getDate() + ((1 + 7 - t.getDay()) % 7 || 7));
        const nextFri = new Date(nextMon);
        nextFri.setDate(nextMon.getDate() + 4);
        return toDateInput(nextFri);
    });
    const [routeGroups, setRouteGroups]             = useState([]);
    const [selectedGroupIds, setSelectedGroupIds]   = useState([]);
    const [customers, setCustomers]                 = useState([]);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
    const [customerDurations, setCustomerDurations] = useState({});
    const [customerSearch, setCustomerSearch]       = useState('');
    const [maxJobsPerDay, setMaxJobsPerDay]         = useState('');
    const [respectPreferences, setRespectPreferences] = useState(true);
    const [isLoadingLastWeek, setIsLoadingLastWeek]   = useState(false);
    const [showWeekPicker, setShowWeekPicker]         = useState(false);
    const [isScanningWeek, setIsScanningWeek]         = useState(false);
    const [existingJobsMap, setExistingJobsMap]       = useState({});
    const [lastWeekLoaded, setLastWeekLoaded]         = useState(false);
    // ── Re-open mode ─────────────────────────────────────────────────────────
    const [weekStatus, setWeekStatus]     = useState(null);
    const [isReopening, setIsReopening]   = useState(false);
    const [isReopen, setIsReopen]         = useState(false);
    const [originalJobsForWeek, setOriginalJobsForWeek] = useState([]);
    const [isSavingReopen, setIsSavingReopen] = useState(false);
    // ── Unassigned jobs in the planning period ────────────────────────────
    const [unassignedInRange, setUnassignedInRange] = useState([]);
    // A1 Guard 1b: multi-crew jobs detected in the scanned range. They can't live in
    // the single-crew grid, so we surface them in a banner (not as draggable stops).
    const [multiCrewInRange, setMultiCrewInRange] = useState([]);
    // ── Auto-optimization state ────────────────────────────────────────────
    const [isOptimizingRoutes, setIsOptimizingRoutes] = useState(false);
    const [optimizeSummary, setOptimizeSummary]       = useState(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [jobTemplates, setJobTemplates]             = useState([]);
    const [customerTemplates, setCustomerTemplates]   = useState({}); // per-customer template overrides

    // ── Step 2 ────────────────────────────────────────────────────
    const [planResult, setPlanResult]   = useState(null);
    const [editableDays, setEditableDays] = useState([]);
    const [dragItem, setDragItem]                 = useState(null);
    const [dragOverCell, setDragOverCell]         = useState(null);
    const [pendingCrewOverride, setPendingCrewOverride] = useState(null);
    const [pendingDayOverride, setPendingDayOverride]   = useState(null);
    const [isPlanning, setIsPlanning]     = useState(false);
    const [unscheduled, setUnscheduled]   = useState([]);

    // ── Step 3 ────────────────────────────────────────────────────
    const [sendEmails, setSendEmails]         = useState(true);
    const [isCreating, setIsCreating]         = useState(false);
    const [createProgress, setCreateProgress] = useState(0);
    const [createTotal, setCreateTotal]       = useState(0);
    const [isDone, setIsDone]                 = useState(false);

    const canManage = can(currentUserProfile, PERMISSIONS.MANAGE_SCHEDULE);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(query(collection(db, `organizations/${orgId}/routeGroups`), orderBy('name')),
            snap => setRouteGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    }, [orgId]);

    useEffect(() => {
        if (!orgId) return;
        // webcustname-7c4a1e92 — every customer, a missing name filled in memory, sorted here.
        return onSnapshot(collection(db, `organizations/${orgId}/customers`),
            snap => setCustomers(snap.docs.map(d => withCustomerName({ id: d.id, ...d.data() })).sort(compareCustomersByName)));
    }, [orgId]);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/jobTemplates`), orderBy('name')),
            snap => setJobTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [orgId]);

    const workingDays = useMemo(() => getWorkingDays(startDate, endDate), [startDate, endDate]);

    useEffect(() => {
        if (orgId && workingDays.length > 0 && customers.length > 0) {
            setExistingJobsMap({});
            scanWeekForExistingJobs();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, startDate, endDate, customers.length]);

    const filteredCustomers = useMemo(() => {
        const q = customerSearch.toLowerCase();
        return customers.filter(c =>
            !q || c.name?.toLowerCase().includes(q) ||
            c.address1?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q)
        );
    }, [customers, customerSearch]);

    const toggleCrew = (id) =>
        setSelectedGroupIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const toggleCustomer = (id) => {
        const isSel = selectedCustomerIds.includes(id);
        setSelectedCustomerIds(prev => isSel ? prev.filter(x => x !== id) : [...prev, id]);
        if (!isSel) {
            const c = customers.find(x => x.id === id);
            const template = jobTemplates.find(t => t.id === selectedTemplateId);
            const defaultDuration = template?.estimatedDuration || c?.estimatedJobDuration || 60;
            setCustomerDurations(prev => ({ ...prev, [id]: prev[id] ?? defaultDuration }));
        }
        setLastWeekLoaded(false);
    };

    const toggleAll = () => {
        if (selectedCustomerIds.length === filteredCustomers.length) {
            setSelectedCustomerIds([]);
        } else {
            setSelectedCustomerIds(filteredCustomers.map(c => c.id));
            setCustomerDurations(prev => {
                const u = { ...prev };
                const globalTemplate = jobTemplates.find(t => t.id === selectedTemplateId);
                filteredCustomers.forEach(c => {
                    if (u[c.id] === undefined) {
                        const custTemplate = jobTemplates.find(t => t.id === customerTemplates[c.id]);
                        u[c.id] = custTemplate?.estimatedDuration ?? globalTemplate?.estimatedDuration ?? c.estimatedJobDuration ?? 60;
                    }
                });
                return u;
            });
        }
    };

    // ── Load last week's customers ────────────────────────────────────────────
    const loadLastWeekCustomers = async (weeksBack = 1) => {
        if (!orgId || !startDate) return;
        setIsLoadingLastWeek(true);
        setShowWeekPicker(false);
        try {
            const referenceEnd = new Date(startDate + 'T00:00:00');
            const rangeEnd   = new Date(referenceEnd);
            rangeEnd.setDate(rangeEnd.getDate() - (weeksBack - 1) * 7);
            const rangeStart = new Date(rangeEnd);
            rangeStart.setDate(rangeStart.getDate() - 7);

            const q = query(
                collection(db, `organizations/${orgId}/jobs`),
                where('startAt', '>=', rangeStart),
                where('startAt', '<',  rangeEnd)
            );
            const snap = await getDocs(q);

            if (!snap || snap.docs.length === 0) {
                alert(`No jobs found for that week. Try a different week.`);
                return;
            }

            const weeksLabel = weeksBack === 1 ? 'last week' : `${weeksBack} weeks ago`;

            // Build per-customer data from actual job docs
            const clientJobMap = {};
            snap.docs.forEach(d => {
                const data = d.data();
                if (!data.clientId) return;
                if (data.status === 'Cancelled' || data.status === 'Canceled') return;
                if (!clientJobMap[data.clientId]) {
                    // Calculate actual duration from startAt/endAt
                    const startMs = data.startAt?.toDate ? data.startAt.toDate().getTime()
                        : data.startAt instanceof Date ? data.startAt.getTime() : null;
                    const endMs = data.endAt?.toDate ? data.endAt.toDate().getTime()
                        : data.endAt instanceof Date ? data.endAt.getTime() : null;
                    const durationMins = (startMs && endMs && endMs > startMs)
                        ? Math.round((endMs - startMs) / 60000)
                        : null;

                    clientJobMap[data.clientId] = {
                        templateId: data.templateId || null,
                        durationMins,
                    };
                }
            });

            const validIds = Object.keys(clientJobMap).filter(id => customers.some(c => c.id === id));

            if (validIds.length === 0) {
                alert("Could not match any previous customers to your customer list.");
                return;
            }

            // Apply per-customer templates from previous week
            const newCustomerTemplates = { ...customerTemplates };
            validIds.forEach(id => {
                const job = clientJobMap[id];
                if (job.templateId && jobTemplates.some(t => t.id === job.templateId)) {
                    newCustomerTemplates[id] = job.templateId;
                }
            });
            setCustomerTemplates(newCustomerTemplates);

            // Set selected template to the most common template from that week
            const templateCounts = {};
            validIds.forEach(id => {
                const tid = clientJobMap[id].templateId;
                if (tid) templateCounts[tid] = (templateCounts[tid] || 0) + 1;
            });
            const mostCommonTemplateId = Object.entries(templateCounts)
                .sort((a, b) => b[1] - a[1])[0]?.[0];
            if (mostCommonTemplateId && !selectedTemplateId) {
                setSelectedTemplateId(mostCommonTemplateId);
            }

            setSelectedCustomerIds(validIds);

            // Apply durations — use actual job duration, fall back to template or customer default
            setCustomerDurations(prev => {
                const updated = { ...prev };
                validIds.forEach(id => {
                    const job = clientJobMap[id];
                    const custTemplate = jobTemplates.find(t => t.id === (newCustomerTemplates[id] || mostCommonTemplateId));
                    updated[id] = job.durationMins
                        ?? custTemplate?.estimatedDuration
                        ?? customers.find(c => c.id === id)?.estimatedJobDuration
                        ?? 60;
                });
                return updated;
            });

            setLastWeekLoaded(true);

        } catch (err) {
            console.error('Failed to load last week customers:', err);
            alert("Could not load previous customers. Please try again.");
        } finally {
            setIsLoadingLastWeek(false);
        }
    };

    // ── Scan selected week for existing recurring jobs + detect planned weeks ────
    const scanWeekForExistingJobs = async () => {
        if (!orgId || workingDays.length === 0) return;
        setIsScanningWeek(true);
        setWeekStatus(null);
        setUnassignedInRange([]); // reset on each scan
        setMultiCrewInRange([]);  // A1: reset multi-crew banner on each scan
        try {
            const generateJobs = httpsCallable(functions, 'generateJobsForDateRange');
            await generateJobs({ orgId, startDate, endDate }).catch(e =>
                console.warn('generateJobsForDateRange non-blocking fail:', e)
            );

            const rangeStart = new Date(startDate + 'T00:00:00');
            const rangeEnd   = new Date(endDate   + 'T23:59:59');
            const q = query(
                collection(db, `organizations/${orgId}/jobs`),
                where('startAt', '>=', rangeStart),
                where('startAt', '<=', rangeEnd)
            );
            const snap = await getDocs(q);

            const newMap = {};
            const plannerJobs = [];
            const unassignedJobsList = [];
            const multiCrewJobsList = [];

            snap.docs.forEach(d => {
                const data = d.data();
                if (!data.clientId) return;
                if (data.status === 'Cancelled' || data.status === 'Canceled' || data.status === 'Completed') return;
                const dateStr = data.startAt?.toDate
                    ? data.startAt.toDate().toISOString().split('T')[0]
                    : (data.startAt instanceof Date ? data.startAt.toISOString().split('T')[0] : null);
                if (!dateStr) return;

                if (!newMap[data.clientId]) newMap[data.clientId] = [];
                newMap[data.clientId].push({
                    jobId:          d.id,
                    date:           dateStr,
                    arrivalWindow:  data.arrivalWindow || 'morning',
                    schedulingMode: data.schedulingMode || 'window',
                    recurringJobId: data.recurringJobId || null,
                    title:          data.title || 'Scheduled Job',
                    status:         data.status,
                });

                if (data.createdVia === 'weekly_planner') {
                    plannerJobs.push({ ...data, id: d.id, _dateStr: dateStr }); // s6costing-docid: doc id wins over a stale stored `id` field
                }

                // Track unassigned jobs — no individual or group assignment.
                // A1: a multi-crew job carries its crews in data.crews[]/line items and
                // may have NO top-level routeGroupId, so it would be falsely flagged
                // unassigned by the check below. Treat multi-crew jobs as assigned.
                const isMultiCrew = (data.crewsTotal || 0) > 1 || (Array.isArray(data.crews) && data.crews.length > 1);
                if (isMultiCrew) {
                    multiCrewJobsList.push({
                        id: d.id,
                        title: data.title || 'Untitled Job',
                        clientName: data.clientName || '',
                        date: dateStr,
                        crewCount: data.crewsTotal || (Array.isArray(data.crews) ? data.crews.length : 0),
                    });
                }
                const hasAssignment = isMultiCrew || (data.assignedTo || []).length > 0 || !!data.routeGroupId;
                if (!hasAssignment) {
                    unassignedJobsList.push({
                        id: d.id,
                        title: data.title || 'Untitled Job',
                        clientName: data.clientName || '',
                        date: dateStr,
                    });
                }
            });

            setExistingJobsMap(newMap);
            setUnassignedInRange(unassignedJobsList);
            setMultiCrewInRange(multiCrewJobsList);

            if (plannerJobs.length > 0) {
                const latestJob = plannerJobs.reduce((latest, j) => {
                    const t = j.createdAt?.toDate ? j.createdAt.toDate() : new Date(0);
                    const lt = latest.createdAt?.toDate ? latest.createdAt.toDate() : new Date(0);
                    return t > lt ? j : latest;
                }, plannerJobs[0]);
                const plannedOn = latestJob.createdAt?.toDate
                    ? latestJob.createdAt.toDate()
                    : null;
                setWeekStatus({ plannedOn, jobCount: plannerJobs.length, jobs: plannerJobs });
            }

            const autoSelectIds = Object.keys(newMap).filter(id =>
                customers.some(c => c.id === id)
            );
            if (autoSelectIds.length > 0) {
                setSelectedCustomerIds(prev => [...new Set([...prev, ...autoSelectIds])]);
                setCustomerDurations(prev => {
                    const updated = { ...prev };
                    autoSelectIds.forEach(id => {
                        if (updated[id] === undefined) {
                            const c = customers.find(x => x.id === id);
                            const globalTemplate = jobTemplates.find(t => t.id === selectedTemplateId);
                            const custTemplate = jobTemplates.find(t => t.id === customerTemplates[id]);
                            updated[id] = custTemplate?.estimatedDuration ?? globalTemplate?.estimatedDuration ?? c?.estimatedJobDuration ?? 60;
                        }
                    });
                    return updated;
                });
            }
        } catch (err) {
            console.error('Week scan failed:', err);
        } finally {
            setIsScanningWeek(false);
        }
    };

    // ── Re-open an already-planned week ──────────────────────────────────────────
    const reopenWeek = async () => {
        if (!weekStatus?.jobs?.length) return;
        setIsReopening(true);
        try {
            setOriginalJobsForWeek(weekStatus.jobs);

            const durOverrides = {};
            weekStatus.jobs.forEach(job => {
                const startMs = job.startAt?.toDate ? job.startAt.toDate().getTime()
                    : job.startAt instanceof Date ? job.startAt.getTime() : null;
                const endMs = job.endAt?.toDate ? job.endAt.toDate().getTime()
                    : job.endAt instanceof Date ? job.endAt.getTime() : null;
                const durationMins = (startMs && endMs && endMs > startMs)
                    ? Math.round((endMs - startMs) / 60000) : 60;
                durOverrides[job.clientId] = durationMins;
            });

            const crewIds = [...new Set(weekStatus.jobs.map(j => j.routeGroupId).filter(Boolean))];
            setSelectedGroupIds(crewIds);

            const clientIds = [...new Set(weekStatus.jobs.map(j => j.clientId).filter(Boolean))];
            setSelectedCustomerIds(clientIds);
            setCustomerDurations(prev => ({ ...prev, ...durOverrides }));

            // FIX (reopen date scatter): constrain the planner's date range to the
            // dates the reopened jobs are ACTUALLY on. Without this, startDate/endDate
            // still span the originally-selected week, so workingDays = the whole week,
            // and Generate Plan re-clusters the jobs across all of it — scattering a
            // one-day schedule. Pinning the range to [minDate, maxDate] of the existing
            // jobs makes workingDays match reality, so clustering stays put.
            const jobDates = weekStatus.jobs
                .map(j => j._dateStr)
                .filter(Boolean)
                .sort();
            if (jobDates.length > 0) {
                setStartDate(jobDates[0]);
                setEndDate(jobDates[jobDates.length - 1]);
            }

            const firstWithTemplate = weekStatus.jobs.find(j => j.templateId);
            if (firstWithTemplate?.templateId) setSelectedTemplateId(firstWithTemplate.templateId);

            setIsReopen(true);
            setWeekStatus(null);
            setStep(1);
        } catch (err) {
            console.error('Reopen failed:', err);
            alert('Could not re-open this week. Please try again.');
        } finally {
            setIsReopening(false);
        }
    };

    // ── Save a re-opened week ─────────────────────────────────────────────────
    const handleSaveReopen = async () => {
        if (!orgId) return;
        setIsSavingReopen(true);
        const globalTemplate = jobTemplates.find(t => t.id === selectedTemplateId) || null;
        const template = globalTemplate; // kept for backwards compat in this function

        const getWindow = (c) => c.preferredWindow === 'morning' ? 'morning'
            : c.preferredWindow === 'afternoon' ? 'afternoon' : 'morning';

        const getWindowTimes = (dateStr, win, durationMins) => {
            const base = new Date(dateStr + 'T00:00:00');
            const awWin = getArrivalWindow(win);
            const [startHour, startMin] = [awWin.startH, awWin.startM];
            const startAt = new Date(base);
            startAt.setHours(startHour, startMin, 0, 0);
            const endAt = new Date(startAt.getTime() + (durationMins || 60) * 60 * 1000);
            return { startAt, endAt };
        };

        try {
            const finalMap = {};
            editableDays.forEach(day => {
                Object.entries(day.crews).forEach(([crewId, cd]) => {
                    cd.customers.forEach(c => {
                        finalMap[`${c.id}_${day.date}`] = { crewId, customer: c, date: day.date };
                    });
                });
            });

            const originalMap = {};
            originalJobsForWeek.forEach(j => {
                originalMap[`${j.clientId}_${j._dateStr}`] = j;
            });

            const batch = writeBatch(db);
            let creates = 0, updates = 0, cancels = 0, skippedMultiCrew = 0;

            originalJobsForWeek.forEach(job => {
                const key = `${job.clientId}_${job._dateStr}`;
                const finalEntry = finalMap[key];

                // A1 Guard 2: multi-crew jobs are managed in their job record, never
                // in the single-crew weekly grid. Intercept them BEFORE the cancel/
                // flatten branches below — otherwise a multi-crew job (which is never
                // in finalMap) would hit the cancel branch and be silently cancelled,
                // or the flatten branch and have its crews[] clobbered. Leave untouched.
                if (isMultiCrewJob(job)) {
                    skippedMultiCrew++;
                    return;
                }

                if (!finalEntry) {
                    batch.update(doc(db, `organizations/${orgId}/jobs`, job.id), {
                        status: 'Cancelled',
                        cancelledAt: serverTimestamp(),
                        cancelledReason: 'Removed during weekly plan revision',
                    });
                    cancels++;
                } else {
                    const durationMins = customerDurations[job.clientId] || 60;
                    const win = template?.defaultWindow && template.defaultWindow !== 'anytime'
                        ? template.defaultWindow
                        : getWindow(finalEntry.customer);
                    const { startAt, endAt } = getWindowTimes(finalEntry.date, win, durationMins);
                    const group = routeGroups.find(g => g.id === finalEntry.crewId);
                    batch.update(doc(db, `organizations/${orgId}/jobs`, job.id), {
                        startAt,
                        endAt,
                        routeGroupId:            finalEntry.crewId,
                        routeGroupName:          group?.name || '',
                        assignedGroupMemberIds:  group?.memberIds || [],
                        assignedTo:              group?.memberIds || [],
                        arrivalWindow:           win,
                        updatedAt:               serverTimestamp(),
                        updatedVia:              'weekly_planner_revision',
                    });
                    updates++;
                }
            });

            // ── Handle new customers — create jobs ─────────────────────────
            const buildLineItems = (durationMins, tmpl) => {
                if (!tmpl?.lineItems?.length) {
                    return [lineIds.newLine({ name: 'Service Visit', description: '', quantity: durationMins ? durationMins / 60 : 1, price: 0, unit: 'Hour', type: 'Service', taxCodeId: '' }, lineIds.newLineId)]; // lineidweb-9e4a2d17
                }
                // lineidweb-9e4a2d17 — the one shared template function (target 'planner': the whole
                // template line incl. its itemId, Hour lines priced by this visit's minutes — what this
                // map did) + a fresh lineId per line.
                return lineIds.templateLinesFor(tmpl.lineItems, { target: 'planner', newId: lineIds.newLineId, durationMins });
            };

            Object.entries(finalMap).forEach(([key, { crewId, customer, date }]) => {
                if (originalMap[key]) return;
                const group = routeGroups.find(g => g.id === crewId);
                const durationMins = customerDurations[customer.id] || customer.estimatedJobDuration || 60;
                const win = template?.defaultWindow && template.defaultWindow !== 'anytime'
                    ? template.defaultWindow : getWindow(customer);
                const { startAt, endAt } = getWindowTimes(date, win, durationMins);
                const custTemplate = jobTemplates.find(t => t.id === customerTemplates[customer.id]) || globalTemplate || null;
                const lineItems = buildLineItems(durationMins, custTemplate);
                const invoiceTotal = lineItems.reduce((s, i) => s + (i.quantity || 0) * (i.price || 0), 0);
                const customerRecord = customers.find(c => c.id === customer.id);
                const stickyNote = (customerRecord?.jobNoteEnabled && customerRecord?.jobNote?.trim())
                    ? customerRecord.jobNote.trim() : '';
                const notes = [template?.defaultNotes, stickyNote].filter(Boolean).join('\n');

                batch.set(doc(collection(db, `organizations/${orgId}/jobs`)), {
                    title: template?.name || 'Service Visit',
                    clientId: customer.id, clientName: customer.name,
                    address: customer.address, status: 'Scheduled', eventType: 'Job',
                    schedulingMode: 'window', arrivalWindow: win,
                    assignmentMode: 'group', routeGroupId: crewId, routeGroupName: group?.name || '',
                    assignedGroupMemberIds: group?.memberIds || [],
                    assignedTo: group?.memberIds || [],
                    assignedToNames: (group?.memberIds || []).map(id => employees?.find(e => e.id === id)?.name || '').filter(Boolean),
                    startAt, endAt, lineItems, invoiceTotal,
                    description: notes || '',
                    templateId: custTemplate?.id || null,
                    jobCosting: { laborEntries: [] }, recurrence: { frequency: 'Never' },
                    createdAt: serverTimestamp(), createdBy: currentUserProfile.id,
                    createdByName: currentUserProfile.name, createdVia: 'weekly_planner',
                });
                creates++;
            });

            await batch.commit();
            setCreateProgress(updates + creates);

            // ── Re-run route optimization ──────────────────────────────────
            setIsOptimizingRoutes(true);
            try {
                const optimizeRoutesFn = httpsCallable(functions, 'optimizeRoutes');
                let totalOrigKm = 0, totalOptKm = 0;

                const weekStart = new Date(startDate + 'T00:00:00');
                const weekEnd   = new Date(endDate   + 'T23:59:59');
                const weekJobsSnap = await getDocs(query(
                    collection(db, `organizations/${orgId}/jobs`),
                    where('startAt', '>=', weekStart),
                    where('startAt', '<=', weekEnd)
                ));
                const jobLookup = {};
                weekJobsSnap.docs.forEach(d => {
                    const data = d.data();
                    if (!data.clientId || data.status === 'Cancelled' || data.status === 'Canceled') return;
                    const dateStr = data.startAt?.toDate
                        ? data.startAt.toDate().toISOString().split('T')[0]
                        : null;
                    if (!dateStr) return;
                    jobLookup[`${data.clientId}_${dateStr}`] = {
                        jobId: d.id, title: data.title, clientName: data.clientName,
                        address: data.address, schedulingMode: data.schedulingMode || 'window',
                        arrivalWindow: data.arrivalWindow || 'morning',
                    };
                });

                const dayCrewMap = {};
                editableDays.forEach(day => {
                    Object.entries(day.crews).forEach(([crewId, cd]) => {
                        if (cd.customers.length === 0) return;
                        if (!dayCrewMap[day.date]) dayCrewMap[day.date] = {};
                        dayCrewMap[day.date][crewId] = cd.customers.map(c => {
                            const realJob = jobLookup[`${c.id}_${day.date}`];
                            return {
                                jobId: realJob?.jobId || c.id,
                                title: realJob?.title || template?.name || 'Service Visit',
                                clientName: realJob?.clientName || c.name,
                                address: realJob?.address || c.address,
                                schedulingMode: 'window',
                                arrivalWindow: realJob?.arrivalWindow || (template?.defaultWindow && template.defaultWindow !== 'anytime' ? template.defaultWindow : (c.preferredWindow || 'morning')),
                            };
                        });
                    });
                });

                // Split into one optimize call per crew so each can anchor on
                // its own `routeGroup.defaultStartingAddress` (depot).
                await Promise.allSettled(Object.entries(dayCrewMap).map(async ([date, crewJobs]) => {
                    await Promise.allSettled(Object.entries(crewJobs).map(async ([crewId, jobs]) => {
                        try {
                            const group = routeGroups.find(g => g.id === crewId);
                            const depot = group?.defaultStartingAddress?.trim();
                            const payload = { orgId, date, crewJobs: { [crewId]: jobs } };
                            if (depot) payload.startingPoint = { address: depot, label: depot };

                            const result = await optimizeRoutesFn(payload);
                            const { results, routeHealth } = result.data;
                            if (routeHealth) {
                                totalOrigKm += routeHealth.originalDistanceKm || 0;
                                totalOptKm  += routeHealth.optimizedDistanceKm || 0;
                            }
                            const data = results[crewId];
                            if (!data?.optimizedJobs?.length) return;
                            await setDoc(doc(db, `organizations/${orgId}/savedRoutes`, `${date}_${crewId}`), {
                                date, routeGroupId: crewId,
                                crewJobs: { [crewId]: data.optimizedJobs },
                                savedAt: serverTimestamp(), savedBy: currentUserProfile.id,
                                autoOptimized: true,
                                ...(depot ? { startingPoint: { mode: 'depot', address: depot, label: depot } } : {}),
                            });
                        } catch (e) { console.warn('Reopen optimize failed for', date, crewId, e); }
                    }));
                }));

                const kmSaved = Math.round((totalOrigKm - totalOptKm) * 10) / 10;
                const pct = totalOrigKm > 0 ? Math.round((1 - totalOptKm / totalOrigKm) * 100) : 0;
                setOptimizeSummary({ kmSaved, pctImprovement: pct, updates, creates, cancels, skippedMultiCrew });
            } catch (e) {
                console.warn('Reopen optimization non-blocking fail:', e);
            } finally {
                setIsOptimizingRoutes(false);
            }

            setIsReopen(false);
            setIsDone(true);
        } catch (err) {
            console.error('Save reopen failed:', err);
            alert('Could not save changes. Please try again.');
        } finally {
            setIsSavingReopen(false);
        }
    };

    const handleRunPlan = async () => {
        if (!orgId || !selectedCustomerIds.length || !workingDays.length || !selectedGroupIds.length) return;
        setIsPlanning(true);
        try {
            const payload = customers.filter(c => selectedCustomerIds.includes(c.id)).map(c => ({
                id: c.id, name: c.name,
                address: buildAddressString(c),
                preferredDays: c.preferredDays || [],
                preferredWindow: c.preferredWindow || 'anytime',
                preferredCrewId: c.preferredCrewId || null,
                estimatedJobDuration: customerDurations[c.id] ?? c.estimatedJobDuration ?? 60,
                hasExistingJobs: !!(existingJobsMap[c.id]?.length),
            }));
            const planWeeklySchedule = httpsCallable(functions, 'planWeeklySchedule');
            const result = await planWeeklySchedule({
                orgId, weekDates: workingDays, routeGroupIds: selectedGroupIds,
                customers: payload,
                maxJobsPerDay: maxJobsPerDay ? Number(maxJobsPerDay) : null,
                respectPreferences,
            });
            setPlanResult(result.data);
            setEditableDays(result.data.days.map(d => ({
                ...d,
                crews: Object.fromEntries(Object.entries(d.crews).map(([k, v]) => [k, {
                    ...v,
                    customers: v.customers.map(c => {
                        const existing = existingJobsMap[c.id];
                        const recurringMatch = existing?.find(j => j.recurringJobId);
                        return {
                            ...c,
                            recurringJobId: recurringMatch?.recurringJobId || null,
                            existingJobDate: existing?.find(j => j.date)?.date || null,
                        };
                    }),
                }])),
            })));
            setUnscheduled(result.data.unscheduled || []);
            setStep(2);
        } catch (err) {
            console.error('Planning failed:', err);
            alert('Planning failed: ' + (err.message || 'Unknown error'));
        } finally {
            setIsPlanning(false);
        }
    };

    const handleDragStart = useCallback((customer, fromDate, fromCrewId) => {
        setDragItem({ customer, fromDate, fromCrewId });
    }, []);

    const executeMove = useCallback((customer, fromDate, fromCrewId, toDate, toCrewId) => {
        setEditableDays(prev => {
            const next = prev.map(d => ({ ...d, crews: Object.fromEntries(Object.entries(d.crews).map(([k, v]) => [k, { ...v, customers: [...v.customers] }])) }));
            const fromDay = next.find(d => d.date === fromDate);
            if (fromDay?.crews[fromCrewId]) {
                fromDay.crews[fromCrewId].customers = fromDay.crews[fromCrewId].customers.filter(c => c.id !== customer.id);
                const ts = fromDay.crews[fromCrewId].customers.reduce((s, c) => s + (c.estimatedJobDuration || 60), 0);
                fromDay.crews[fromCrewId].stats = { ...fromDay.crews[fromCrewId].stats, jobCount: fromDay.crews[fromCrewId].customers.length, totalServiceMinutes: ts, totalMinutes: ts + (fromDay.crews[fromCrewId].stats?.driveMinutes || 0) };
            }
            const toDay = next.find(d => d.date === toDate);
            if (!toDay) return prev;
            if (!toDay.crews[toCrewId]) {
                const g = routeGroups.find(g => g.id === toCrewId);
                toDay.crews[toCrewId] = { crewId: toCrewId, crewName: g?.name || '', customers: [], stats: { jobCount: 0, totalServiceMinutes: 0, driveMinutes: 0, totalMinutes: 0, driveKm: 0, isOverCapacity: false } };
            }
            toDay.crews[toCrewId].customers.push({ ...customer, preferredCrewId: customer.preferredCrewId });
            const ts2 = toDay.crews[toCrewId].customers.reduce((s, c) => s + (c.estimatedJobDuration || 60), 0);
            toDay.crews[toCrewId].stats = { ...toDay.crews[toCrewId].stats, jobCount: toDay.crews[toCrewId].customers.length, totalServiceMinutes: ts2, totalMinutes: ts2 + (toDay.crews[toCrewId].stats?.driveMinutes || 0) };
            return next;
        });
    }, [routeGroups]);

    const handleDrop = useCallback((toDate, toCrewId) => {
        if (!dragItem) return;
        const { customer, fromDate, fromCrewId } = dragItem;
        if (fromDate === toDate && fromCrewId === toCrewId) { setDragItem(null); setDragOverCell(null); return; }

        const isCrewChange = fromCrewId !== toCrewId;
        const hasCrewLock = customer.preferredCrewId && customer.preferredCrewId !== toCrewId;
        if (isCrewChange && hasCrewLock) {
            setPendingCrewOverride({ customer, fromDate, fromCrewId, toDate, toCrewId });
            setDragItem(null);
            setDragOverCell(null);
            return;
        }

        const isDayChange = fromDate !== toDate;
        const toDateDayName = new Date(toDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const hasPreferredDays = customer.preferredDays?.length > 0;
        const isOffPreferredDay = hasPreferredDays && !customer.preferredDays.includes(toDateDayName);
        if (isDayChange && isOffPreferredDay) {
            setPendingDayOverride({ customer, fromDate, fromCrewId, toDate, toCrewId });
            setDragItem(null);
            setDragOverCell(null);
            return;
        }

        executeMove(customer, fromDate, fromCrewId, toDate, toCrewId);
        setDragItem(null);
        setDragOverCell(null);
    }, [dragItem, executeMove]);

    const confirmCrewOverride = useCallback(() => {
        if (!pendingCrewOverride) return;
        const { customer, fromDate, fromCrewId, toDate, toCrewId } = pendingCrewOverride;
        executeMove(customer, fromDate, fromCrewId, toDate, toCrewId);
        setPendingCrewOverride(null);
    }, [pendingCrewOverride, executeMove]);

    const cancelCrewOverride = useCallback(() => {
        setPendingCrewOverride(null);
    }, []);

    const confirmDayOverride = useCallback(() => {
        if (!pendingDayOverride) return;
        const { customer, fromDate, fromCrewId, toDate, toCrewId } = pendingDayOverride;
        executeMove(customer, fromDate, fromCrewId, toDate, toCrewId);
        setPendingDayOverride(null);
    }, [pendingDayOverride, executeMove]);

    const cancelDayOverride = useCallback(() => {
        setPendingDayOverride(null);
    }, []);

    const openMoveSheet  = useCallback((customer, fromDate, fromCrewId) => {
        setMoveSheet({ customer, fromDate, fromCrewId });
    }, []);
    const closeMoveSheet = useCallback(() => setMoveSheet(null), []);

    const executeMoveFromSheet = useCallback((toDate, toCrewId) => {
        if (!moveSheet) return;
        const { customer, fromDate, fromCrewId } = moveSheet;
        closeMoveSheet();

        if (fromDate === toDate && fromCrewId === toCrewId) return;

        const isCrewChange = fromCrewId !== toCrewId;
        const hasCrewLock = customer.preferredCrewId && customer.preferredCrewId !== toCrewId;
        if (isCrewChange && hasCrewLock) {
            setPendingCrewOverride({ customer, fromDate, fromCrewId, toDate, toCrewId });
            return;
        }
        const isDayChange = fromDate !== toDate;
        const toDateDayName = new Date(toDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const isOffPreferredDay = customer.preferredDays?.length > 0 && !customer.preferredDays.includes(toDateDayName);
        if (isDayChange && isOffPreferredDay) {
            setPendingDayOverride({ customer, fromDate, fromCrewId, toDate, toCrewId });
            return;
        }
        executeMove(customer, fromDate, fromCrewId, toDate, toCrewId);
    }, [moveSheet, closeMoveSheet, executeMove]);

    const handleMoveCustomer = useCallback((date, crewId, customer, fromIdx, toIdx) => {
        setEditableDays(prev => {
            const next = prev.map(d => ({ ...d, crews: Object.fromEntries(Object.entries(d.crews).map(([k, v]) => [k, { ...v, customers: [...v.customers] }])) }));
            const day = next.find(d => d.date === date);
            if (!day?.crews[crewId]) return prev;
            const list = day.crews[crewId].customers;
            if (toIdx < 0 || toIdx >= list.length) return prev;
            const updated = [...list];
            const [removed] = updated.splice(fromIdx, 1);
            updated.splice(toIdx, 0, removed);
            day.crews[crewId].customers = updated;
            return next;
        });
    }, []);

    const handleCreateJobs = async () => {
        if (!orgId) return;
        setIsCreating(true);
        const getWindow = (c) => c.preferredWindow === 'morning' ? 'morning' : c.preferredWindow === 'afternoon' ? 'afternoon' : 'morning';
        const getWindowTimes = (dateStr, win, durationMins) => {
            const base = new Date(dateStr + 'T00:00:00');
            const awWin = getArrivalWindow(win);
            const [startHour, startMin] = [awWin.startH, awWin.startM];
            const startAt = new Date(base);
            startAt.setHours(startHour, startMin, 0, 0);
            const mins = durationMins || 60;
            const endAt = new Date(startAt.getTime() + mins * 60 * 1000);
            return { startAt, endAt };
        };
        const allJobs = [];
        editableDays.forEach(day => {
            Object.entries(day.crews).forEach(([crewId, cd]) => {
                cd.customers.forEach(customer => allJobs.push({ day, crewId, customer }));
            });
        });
        setCreateTotal(allJobs.length);
        setCreateProgress(0);

        const globalTemplate = jobTemplates.find(t => t.id === selectedTemplateId) || null;

        const getTemplateForCustomer = (customerId) =>
            jobTemplates.find(t => t.id === customerTemplates[customerId]) || globalTemplate;

        const buildLineItems = (durationMins, template) => {
            if (!template?.lineItems?.length) {
                return [lineIds.newLine({ name: 'Service Visit', description: '', quantity: durationMins ? durationMins / 60 : 1, price: 0, unit: 'Hour', type: 'Service', taxCodeId: '' }, lineIds.newLineId)]; // lineidweb-9e4a2d17
            }
            // lineidweb-9e4a2d17 — the one shared template function (target 'planner'), as above
            return lineIds.templateLinesFor(template.lineItems, { target: 'planner', newId: lineIds.newLineId, durationMins });
        };

        const BATCH_SIZE = 20;
        let created = 0;
        for (let i = 0; i < allJobs.length; i += BATCH_SIZE) {
            const batch = writeBatch(db);
            allJobs.slice(i, i + BATCH_SIZE).forEach(({ day, crewId, customer }) => {
                const group = routeGroups.find(g => g.id === crewId);
                const template = getTemplateForCustomer(customer.id);
                const durationMins = customerDurations[customer.id] ?? template?.estimatedDuration ?? customer.estimatedJobDuration ?? 60;

                const existingForCustomer = existingJobsMap[customer.id] || [];
                const alreadyBooked = existingForCustomer.some(j => j.date === day.date);
                if (alreadyBooked) return;
                const win = template?.defaultWindow && template.defaultWindow !== 'anytime'
                    ? template.defaultWindow
                    : getWindow(customer);
                const { startAt, endAt } = getWindowTimes(day.date, win, durationMins);
                const lineItems = buildLineItems(durationMins, template);
                const invoiceTotal = lineItems.reduce((s, i) => s + (i.quantity || 0) * (i.price || 0), 0);

                const customerRecord = customers.find(c => c.id === customer.id);
                const stickyNote = (customerRecord?.jobNoteEnabled && customerRecord?.jobNote?.trim())
                    ? customerRecord.jobNote.trim()
                    : '';
                const notes = [template?.defaultNotes, stickyNote].filter(Boolean).join('\n');

                batch.set(doc(collection(db, `organizations/${orgId}/jobs`)), {
                    title: template?.name || 'Service Visit',
                    clientId: customer.id, clientName: customer.name,
                    address: customer.address, status: 'Scheduled', eventType: 'Job',
                    schedulingMode: 'window', arrivalWindow: win,
                    assignmentMode: 'group', routeGroupId: crewId, routeGroupName: group?.name || '',
                    assignedGroupMemberIds: group?.memberIds || [],
                    assignedTo: group?.memberIds || [],
                    assignedToNames: (group?.memberIds || []).map(id => employees?.find(e => e.id === id)?.name || '').filter(Boolean),
                    startAt, endAt,
                    lineItems,
                    invoiceTotal,
                    description: notes || '',
                    templateId: template?.id || null,
                    jobCosting: { laborEntries: [] }, recurrence: { frequency: 'Never' },
                    createdAt: serverTimestamp(), createdBy: currentUserProfile.id,
                    createdByName: currentUserProfile.name, createdVia: 'weekly_planner',
                });
            });
            await batch.commit();
            created += Math.min(BATCH_SIZE, allJobs.length - i);
            setCreateProgress(created);
        }
        if (sendEmails) {
            const sendJobNotification = httpsCallable(functions, 'sendJobNotification');
            allJobs.forEach(({ day, customer }) => {
                const win = getWindow(customer);
                const { startAt } = getWindowTimes(day.date, win);
                sendJobNotification({ type: 'booking_confirmation', clientId: customer.id, orgId, jobTitle: 'Service Visit', schedulingMode: 'window', arrivalWindow: win, startAt: startAt.toISOString() })
                    .catch(e => console.warn('Email non-blocking fail:', e));
            });
        }
        setIsCreating(false);

        setIsOptimizingRoutes(true);
        try {
            const optimizeRoutesFn = httpsCallable(functions, 'optimizeRoutes');
            let totalOrigKm = 0;
            let totalOptKm  = 0;

            const weekStart = new Date(startDate + 'T00:00:00');
            const weekEnd   = new Date(endDate   + 'T23:59:59');
            const weekJobsSnap = await getDocs(query(
                collection(db, `organizations/${orgId}/jobs`),
                where('startAt', '>=', weekStart),
                where('startAt', '<=', weekEnd)
            ));

            const jobLookup = {};
            weekJobsSnap.docs.forEach(d => {
                const data = d.data();
                if (!data.clientId) return;
                const dateStr = data.startAt?.toDate
                    ? data.startAt.toDate().toISOString().split('T')[0]
                    : (data.startAt instanceof Date ? data.startAt.toISOString().split('T')[0] : null);
                if (!dateStr) return;
                const key = `${data.clientId}_${dateStr}`;
                jobLookup[key] = {
                    jobId:          d.id,
                    title:          data.title || 'Service Visit',
                    clientName:     data.clientName || '',
                    address:        data.address || '',
                    schedulingMode: data.schedulingMode || 'window',
                    arrivalWindow:  data.arrivalWindow || 'morning',
                };
            });

            const dayCrewMap = {};
            editableDays.forEach(day => {
                Object.entries(day.crews).forEach(([crewId, cd]) => {
                    if (cd.customers.length === 0) return;
                    if (!dayCrewMap[day.date]) dayCrewMap[day.date] = {};
                    dayCrewMap[day.date][crewId] = cd.customers.map(c => {
                        const key = `${c.id}_${day.date}`;
                        const realJob = jobLookup[key];
                        const custTmpl = getTemplateForCustomer(c.id);
                        return {
                            jobId:          realJob?.jobId  || c.id,
                            title:          realJob?.title  || custTmpl?.name || 'Service Visit',
                            clientName:     realJob?.clientName || c.name,
                            address:        realJob?.address || c.address,
                            schedulingMode: realJob?.schedulingMode || 'window',
                            arrivalWindow:  realJob?.arrivalWindow ||
                                (custTmpl?.defaultWindow && custTmpl.defaultWindow !== 'anytime'
                                    ? custTmpl.defaultWindow
                                    : (c.preferredWindow || 'morning')),
                        };
                    });
                });
            });

            // Split into one optimize call per crew so each can anchor on
            // its own `routeGroup.defaultStartingAddress` (depot).
            await Promise.allSettled(Object.entries(dayCrewMap).map(async ([date, crewJobsForDay]) => {
                await Promise.allSettled(Object.entries(crewJobsForDay).map(async ([crewId, jobs]) => {
                    try {
                        const group = routeGroups.find(g => g.id === crewId);
                        const depot = group?.defaultStartingAddress?.trim();
                        const payload = { orgId, date, crewJobs: { [crewId]: jobs } };
                        if (depot) payload.startingPoint = { address: depot, label: depot };

                        const result = await optimizeRoutesFn(payload);
                        const { results, routeHealth } = result.data;
                        if (routeHealth) {
                            totalOrigKm += routeHealth.originalDistanceKm || 0;
                            totalOptKm  += routeHealth.optimizedDistanceKm || 0;
                        }
                        const data = results[crewId];
                        if (!data?.optimizedJobs?.length) return;
                        const routeId = `${date}_${crewId}`;
                        await setDoc(doc(db, `organizations/${orgId}/savedRoutes`, routeId), {
                            date,
                            routeGroupId: crewId,
                            crewJobs: { [crewId]: data.optimizedJobs },
                            savedAt: serverTimestamp(),
                            savedBy: currentUserProfile.id,
                            autoOptimized: true,
                            ...(depot ? { startingPoint: { mode: 'depot', address: depot, label: depot } } : {}),
                        });
                    } catch (dayErr) {
                        console.warn(`Auto-optimize failed for ${date} crew ${crewId}:`, dayErr);
                    }
                }));
            }));

            const kmSaved = Math.round((totalOrigKm - totalOptKm) * 10) / 10;
            const pct = totalOrigKm > 0 ? Math.round((1 - totalOptKm / totalOrigKm) * 100) : 0;
            setOptimizeSummary({ kmSaved, pctImprovement: pct });
        } catch (optErr) {
            console.warn('Auto-optimization failed (non-blocking):', optErr);
        } finally {
            setIsOptimizingRoutes(false);
        }

        setIsDone(true);
    };

    const selectedGroups = routeGroups.filter(g => selectedGroupIds.includes(g.id));
    const totalJobs = editableDays.reduce((s, d) => s + Object.values(d.crews).reduce((cs, c) => cs + c.customers.length, 0), 0);
    const totalMins = editableDays.reduce((s, d) => s + Object.values(d.crews).reduce((cs, c) => cs + (c.stats?.totalMinutes || 0), 0), 0);
    const totalDriveMins = editableDays.reduce((s, d) => s + Object.values(d.crews).reduce((cs, c) => cs + (c.stats?.driveMinutes || 0), 0), 0);

    if (!canManage) return (
        <div className="p-8 text-center">
            <AlertTriangle size={32} className="text-amber-400 mx-auto mb-3" />
            <p className="text-gray-500">You don't have permission to use the Weekly Planner.</p>
        </div>
    );

    if (isDone) return (
        <div className="p-6 md:p-10 max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {optimizeSummary?.updates !== undefined ? 'Week Updated & Re-optimized' : 'Week Scheduled & Optimized'}
            </h2>
            <p className="text-gray-500 mb-4">
                {optimizeSummary?.updates !== undefined ? (
                    <>
                        {optimizeSummary.updates} job{optimizeSummary.updates !== 1 ? 's' : ''} updated
                        {optimizeSummary.creates > 0 && `, ${optimizeSummary.creates} new`}
                        {optimizeSummary.cancels > 0 && `, ${optimizeSummary.cancels} cancelled`}
                        {optimizeSummary.skippedMultiCrew > 0 && `, ${optimizeSummary.skippedMultiCrew} multi-crew job${optimizeSummary.skippedMultiCrew !== 1 ? 's' : ''} left untouched`}
                        {' '}across {editableDays.filter(d => Object.values(d.crews).some(c => c.customers.length > 0)).length} days.
                    </>
                ) : (
                    <>{createProgress} jobs created across {editableDays.filter(d => Object.values(d.crews).some(c => c.customers.length > 0)).length} days and {selectedGroups.length} {selectedGroups.length === 1 ? 'crew' : 'crews'}.
                    {sendEmails && ' Confirmation emails are sending.'}</>
                )}
            </p>
            {optimizeSummary && optimizeSummary.kmSaved > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-5 text-left">
                    <div className="flex items-center gap-2 mb-1">
                        <Zap size={16} className="text-green-600 flex-shrink-0" />
                        <p className="text-sm font-bold text-green-800">Routes optimized — {optimizeSummary.pctImprovement}% more efficient</p>
                    </div>
                    <p className="text-xs text-green-700 ml-6">
                        Saved <strong>{optimizeSummary.kmSaved} km</strong> of driving across the week vs booking order.
                        Each crew's daily route is ready — open Routes any morning and the stop order is already set.
                    </p>
                </div>
            )}
            {optimizeSummary && optimizeSummary.kmSaved === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-5 text-left">
                    <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-blue-500 flex-shrink-0" />
                        <p className="text-xs text-blue-700">Routes are already in an efficient order. Daily routes are saved and ready for your crews.</p>
                    </div>
                </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`/routes?date=${editableDays.find(d => Object.values(d.crews || {}).some(c => c.customers?.length > 0) || d.customers?.length > 0)?.date || startDate}`}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 text-sm">
                    View in Routes
                </a>
                <button onClick={() => {
                    setStep(1); setIsDone(false); setPlanResult(null); setEditableDays([]);
                    setSelectedCustomerIds([]); setOptimizeSummary(null); setLastWeekLoaded(false);
                    setExistingJobsMap({}); setWeekStatus(null); setIsReopen(false);
                    setOriginalJobsForWeek([]); setUnassignedInRange([]); setMultiCrewInRange([]);
                }}
                    className="px-6 py-2.5 border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 text-sm">
                    Plan Another Week
                </button>
            </div>
        </div>
    );

    return (
        <div className="p-4 sm:p-6 md:p-8 w-full min-w-0">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Weekly Planner</h1>
                <p className="text-sm text-gray-500 mt-1">Cluster customers geographically across multiple crews and days — create all jobs in one step.</p>
            </div>
            <StepIndicator step={step} />

            {/* ── STEP 1 ───────────────────────────────────────────── */}
            {step === 1 && (
                <div className="space-y-5 max-w-2xl">
                    {/* Reopen mode indicator */}
                    {isReopen && (
                        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <FolderOpen size={16} className="text-amber-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-amber-900">Editing existing plan</p>
                                <p className="text-xs text-amber-700 mt-0.5">Original customers are pre-selected. Add or remove customers, adjust durations, then hit Generate Plan to continue.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setIsReopen(false); setOriginalJobsForWeek([]); }}
                                className="text-xs text-amber-600 hover:text-amber-800 font-medium flex-shrink-0"
                            >
                                Cancel
                            </button>
                        </div>
                    )}

                    {/* A1 Guard 1b: multi-crew jobs in range — informational, not a warning.
                        These can't live in the single-crew grid; they're managed in their job
                        records. Blue (calm) to distinguish from the amber unassigned warning. */}
                    {multiCrewInRange.length > 0 && !isScanningWeek && (
                        <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
                            <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-blue-900">
                                    {multiCrewInRange.length} multi-crew {multiCrewInRange.length === 1 ? 'job' : 'jobs'} scheduled in this period
                                </p>
                                <p className="text-xs text-blue-700 mt-0.5">
                                    {multiCrewInRange.length === 1 ? 'This job is' : 'These jobs are'} assigned to multiple crews and {multiCrewInRange.length === 1 ? 'is' : 'are'} managed in {multiCrewInRange.length === 1 ? 'its' : 'their'} job {multiCrewInRange.length === 1 ? 'record' : 'records'} — not editable in the weekly grid. Shown here so this period reads accurately: {multiCrewInRange.map(j => j.clientName).filter(Boolean).join(', ')}.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Unassigned jobs warning */}
                    {unassignedInRange.length > 0 && !isScanningWeek && (
                        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-amber-900">
                                    {unassignedInRange.length} unassigned {unassignedInRange.length === 1 ? 'job' : 'jobs'} in this period
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    {unassignedInRange.length === 1 ? 'This job has' : 'These jobs have'} no crew assignment and won't appear in route planning. Go to Routes to assign {unassignedInRange.length === 1 ? 'it' : 'them'}, or include the customers below to incorporate them into this plan.
                                </p>
                            </div>
                            <a
                                href={`/routes?date=${startDate}`}
                                className="text-xs text-amber-700 font-semibold hover:text-amber-900 flex-shrink-0 underline whitespace-nowrap"
                            >
                                View in Routes →
                            </a>
                        </div>
                    )}

                    {/* Date range */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                            <CalendarDays size={16} className="text-blue-500" /> Date Range
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Start</label>
                                <input type="date" value={startDate} onChange={e => {
                                        const val = e.target.value;
                                        setStartDate(val);
                                        if (val) {
                                            const d = new Date(val + 'T00:00:00');
                                            d.setDate(d.getDate() + 4);
                                            setEndDate(toDateInput(d));
                                        }
                                    }}
                                    className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">End</label>
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                        </div>
                        {workingDays.length > 0 && (
                            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                <Info size={11} />
                                {workingDays.length} working {workingDays.length === 1 ? 'day' : 'days'}: {workingDays.map(d => capitalize(format(parseISO(d), 'EEE MMM d'))).join(', ')}
                            </p>
                        )}
                    </div>

                    {/* Crew multi-select */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Users size={16} className="text-blue-500" /> Select Crews
                            {selectedGroupIds.length > 0 && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{selectedGroupIds.length} selected</span>
                            )}
                        </h3>
                        {routeGroups.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No route groups. Create them in Settings → Route Groups.</p>
                        ) : (
                            <div className="space-y-2">
                                {routeGroups.map((g, idx) => {
                                    const isSel = selectedGroupIds.includes(g.id);
                                    const color = CREW_COLORS[idx % CREW_COLORS.length];
                                    return (
                                        <button key={g.id} type="button" onClick={() => toggleCrew(g.id)}
                                            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border text-left transition-all ${
                                                isSel ? 'bg-blue-50 border-blue-300 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300'
                                            }`}>
                                            <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${isSel ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}>
                                                {isSel && <CheckCircle size={10} className="text-white" />}
                                            </div>
                                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-800">{g.name}</p>
                                                <p className="text-xs text-gray-400">{(g.memberIds || []).length} members</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Max Stops / Day / Crew <span className="font-normal text-gray-400">(optional)</span></label>
                                <input type="number" min="1" value={maxJobsPerDay} onChange={e => setMaxJobsPerDay(e.target.value)}
                                    placeholder="Auto" className="w-full p-2.5 border rounded-lg text-sm outline-none" />
                            </div>
                            <div className="flex flex-col justify-end">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <button type="button" onClick={() => setRespectPreferences(p => !p)}
                                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${respectPreferences ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${respectPreferences ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </button>
                                    <span className="text-xs font-medium text-gray-700">Respect preferences</span>
                                </label>
                                <p className="text-[10px] text-gray-400 mt-1">Day prefs + crew locks from customer records</p>
                            </div>
                        </div>
                    </div>

                    {/* Job Template picker */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                            <FileText size={16} className="text-blue-500" /> Default Job Template
                            <span className="text-xs font-normal text-gray-400">— applies to all customers unless overridden</span>
                        </h3>
                        <p className="text-xs text-gray-400 mb-3">Type to search. You can override per-customer in the durations section below.</p>
                        {jobTemplates.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">
                                No templates yet. Create them in <strong>Settings → Job Templates</strong> to pre-fill job titles, line items, and notes.
                            </p>
                        ) : (
                            <>
                                <TemplateCombobox
                                    templates={jobTemplates}
                                    value={selectedTemplateId}
                                    onChange={(id) => {
                                        setSelectedTemplateId(id);
                                        const t = jobTemplates.find(t => t.id === id);
                                        if (t?.estimatedDuration && selectedCustomerIds.length > 0) {
                                            setCustomerDurations(prev => {
                                                const updated = { ...prev };
                                                selectedCustomerIds.forEach(cid => {
                                                    if (!customerTemplates[cid]) updated[cid] = t.estimatedDuration;
                                                });
                                                return updated;
                                            });
                                        }
                                    }}
                                    placeholder="Search or select a default template…"
                                />
                                {selectedTemplateId && selectedCustomerIds.length > 0 && (
                                    <button type="button"
                                        onClick={() => {
                                            const t = jobTemplates.find(t => t.id === selectedTemplateId);
                                            if (!t) return;
                                            setCustomerDurations(prev => {
                                                const updated = { ...prev };
                                                selectedCustomerIds.forEach(id => { updated[id] = t.estimatedDuration || 60; });
                                                return updated;
                                            });
                                            setCustomerTemplates(prev => {
                                                const updated = { ...prev };
                                                selectedCustomerIds.forEach(id => { delete updated[id]; });
                                                return updated;
                                            });
                                        }}
                                        className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                                        ↺ Apply to all customers (resets overrides)
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Customer selection */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                <MapPin size={16} className="text-blue-500" /> Customers
                                {selectedCustomerIds.length > 0 && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{selectedCustomerIds.length}</span>
                                )}
                                {lastWeekLoaded && (
                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                        <History size={10} /> copied from previous week
                                    </span>
                                )}
                            </h3>
                            <div className="flex items-center gap-2">
                                <div className="relative" data-week-picker>
                                    <button
                                        type="button"
                                        onClick={() => setShowWeekPicker(p => !p)}
                                        disabled={isLoadingLastWeek || !startDate}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isLoadingLastWeek
                                            ? <><Loader2 size={11} className="animate-spin" /> Loading…</>
                                            : <><History size={11} /> Copy previous week <ChevronDown size={11} /></>
                                        }
                                    </button>
                                    {showWeekPicker && startDate && (
                                        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl w-56 overflow-hidden">
                                            <p className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b">Select a week to copy</p>
                                            {[1, 2, 3, 4].map(w => {
                                                const ref = new Date(startDate + 'T00:00:00');
                                                const wEnd = new Date(ref);
                                                wEnd.setDate(ref.getDate() - (w - 1) * 7 - 1);
                                                const wStart = new Date(wEnd);
                                                wStart.setDate(wEnd.getDate() - 6);
                                                const fmt = (d) => d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
                                                const label = w === 1 ? 'Last week' : `${w} weeks ago`;
                                                return (
                                                    <button key={w} type="button"
                                                        onClick={() => loadLastWeekCustomers(w)}
                                                        className="w-full text-left px-3 py-2.5 hover:bg-purple-50 transition-colors border-b border-gray-50 last:border-0">
                                                        <p className="text-sm font-medium text-gray-800">{label}</p>
                                                        <p className="text-xs text-gray-400">{fmt(wStart)} – {fmt(wEnd)}</p>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <button type="button" onClick={toggleAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                                    {selectedCustomerIds.length === filteredCustomers.length ? 'Deselect all' : 'Select all'}
                                </button>
                            </div>
                        </div>
                        <div className="relative mb-3">
                            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
                            <input type="text" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
                                placeholder="Search by name, address, city…"
                                className="w-full pl-9 pr-8 p-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                            {customerSearch && (
                                <button onClick={() => setCustomerSearch('')} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"><X size={16} /></button>
                            )}
                        </div>
                        <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                            {filteredCustomers.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-6">No customers found.</p>
                            ) : filteredCustomers.map(c => {
                                const isSel = selectedCustomerIds.includes(c.id);
                                const addr = [c.address1, c.city].filter(Boolean).join(', ');
                                const lockedCrew = c.preferredCrewId ? routeGroups.find(g => g.id === c.preferredCrewId) : null;
                                return (
                                    <button key={c.id} type="button" onClick={() => toggleCustomer(c.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                            isSel && existingJobsMap[c.id] ? 'bg-green-50' :
                                            isSel ? 'bg-blue-50' :
                                            existingJobsMap[c.id] ? 'bg-green-50/40 hover:bg-green-50' :
                                            'bg-white hover:bg-gray-50'
                                        }`}>
                                        <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border ${isSel ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                            {isSel && <CheckCircle size={10} className="text-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                                            {addr && <p className="text-xs text-gray-400 truncate">{addr}</p>}
                                        </div>
                                        {existingJobsMap[c.id] && (
                                            <span className="text-[10px] text-green-700 bg-green-100 border border-green-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 flex-shrink-0 font-semibold">
                                                <Repeat size={8} /> recurring
                                            </span>
                                        )}
                                        {lockedCrew && (
                                            selectedGroupIds.includes(lockedCrew.id) ? (
                                                <span className="text-[10px] text-blue-600 flex items-center gap-0.5 flex-shrink-0">
                                                    <Lock size={9} /> {lockedCrew.name}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-amber-600 flex items-center gap-0.5 flex-shrink-0" title={`Prefers ${lockedCrew.name} — not selected`}>
                                                    <AlertTriangle size={9} /> {lockedCrew.name} not selected
                                                </span>
                                            )
                                        )}
                                        {c.preferredDays?.length > 0 && (
                                            <span className="text-[10px] text-gray-400 flex-shrink-0">{c.preferredDays.map(d => d.slice(0,3)).join('/')}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Duration editor */}
                    {selectedCustomerIds.length > 0 && (
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                                <Clock size={16} className="text-blue-500" /> Job Durations This Week
                            </h3>
                            <p className="text-xs text-gray-400 mb-4">Pre-filled from defaults. Adjust for anything extra this week — new sod, extra trimming, first visit of the season, etc.</p>
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                                {customers.filter(c => selectedCustomerIds.includes(c.id)).map(c => {
                                    const custTemplateId = customerTemplates[c.id] || '';
                                    return (
                                    <div key={c.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                                        <p className="text-sm text-gray-700 w-28 flex-shrink-0 truncate" title={c.name}>{c.name}</p>
                                        <div className="flex-1 min-w-0">
                                            <TemplateCombobox
                                                templates={jobTemplates}
                                                value={custTemplateId}
                                                onChange={(id) => {
                                                    setCustomerTemplates(prev => {
                                                        const next = { ...prev };
                                                        if (id) next[c.id] = id; else delete next[c.id];
                                                        return next;
                                                    });
                                                    const t = jobTemplates.find(t => t.id === id);
                                                    const globalT = jobTemplates.find(t => t.id === selectedTemplateId);
                                                    const dur = t?.estimatedDuration ?? globalT?.estimatedDuration ?? c.estimatedJobDuration ?? 60;
                                                    setCustomerDurations(prev => ({ ...prev, [c.id]: dur }));
                                                }}
                                                placeholder={selectedTemplateId ? jobTemplates.find(t=>t.id===selectedTemplateId)?.name || 'Using default' : 'Using default'}
                                                size="sm"
                                            />
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <input type="number" min="5" step="5"
                                                value={customerDurations[c.id] ?? c.estimatedJobDuration ?? 60}
                                                onChange={e => setCustomerDurations(prev => ({ ...prev, [c.id]: e.target.value ? parseInt(e.target.value) : 60 }))}
                                                className="w-16 p-1.5 border rounded-md text-sm text-center" />
                                            <span className="text-xs text-gray-400">min</span>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Scanning indicator */}
                    {isScanningWeek && (
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-500">
                            <RefreshCw size={13} className="animate-spin flex-shrink-0" />
                            Scanning week for existing bookings…
                        </div>
                    )}

                    {/* ── ALREADY PLANNED BANNER ─────────────────────────── */}
                    {weekStatus && !isScanningWeek && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden">
                            <div className="px-4 pt-4 pb-3">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                        <CalendarCheck size={18} className="text-amber-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-amber-900">This week has already been planned</p>
                                        <p className="text-xs text-amber-700 mt-0.5">
                                            {weekStatus.jobCount} job{weekStatus.jobCount !== 1 ? 's' : ''} were scheduled
                                            {weekStatus.plannedOn ? ` on ${weekStatus.plannedOn.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}` : ''}.
                                            Re-open to adjust the plan, or start fresh.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 px-4 pb-4">
                                <button
                                    type="button"
                                    onClick={reopenWeek}
                                    disabled={isReopening}
                                    className="flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                                >
                                    {isReopening
                                        ? <><Loader2 size={15} className="animate-spin" /> Opening…</>
                                        : <><FolderOpen size={15} /> Re-open this week</>
                                    }
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setWeekStatus(null)}
                                    className="flex items-center justify-center gap-2 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl font-semibold text-sm transition-colors"
                                >
                                    <Trash2 size={15} className="text-gray-400" /> Start fresh
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Recurring jobs info */}
                    {Object.keys(existingJobsMap).length > 0 && !isScanningWeek && !weekStatus && (
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-xs text-green-700">
                            <Repeat size={13} className="flex-shrink-0" />
                            <span><strong>{Object.keys(existingJobsMap).length}</strong> customer{Object.keys(existingJobsMap).length !== 1 ? 's' : ''} already have jobs booked this week — auto-selected in green. These won't be duplicated.</span>
                        </div>
                    )}

                    <button onClick={handleRunPlan}
                        disabled={isPlanning || !selectedCustomerIds.length || !workingDays.length || !selectedGroupIds.length}
                        className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm transition-colors">
                        {isPlanning
                            ? <><Loader2 size={18} className="animate-spin" /> Clustering {selectedCustomerIds.length} customers across {selectedGroupIds.length} {selectedGroupIds.length === 1 ? 'crew' : 'crews'}…</>
                            : <><Zap size={18} /> Generate Plan — {selectedCustomerIds.length} customers · {selectedGroupIds.length} {selectedGroupIds.length === 1 ? 'crew' : 'crews'} · {workingDays.length} days</>
                        }
                    </button>
                </div>
            )}

            {/* ── STEP 2 — DAY × CREW GRID ─────────────────────────── */}
            {step === 2 && (
                <div>
                    {isReopen && (
                        <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                            <FolderOpen size={12} className="flex-shrink-0" />
                            <span>Editing existing plan — drag to reassign. Hit <strong>Save Changes</strong> when done.</span>
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-gray-500">
                        <span><strong>{totalJobs}</strong> jobs · <strong>{selectedGroups.length}</strong> {selectedGroups.length === 1 ? 'crew' : 'crews'} · <strong>{editableDays.length}</strong> days</span>
                        <span className="text-gray-300">·</span>
                        <span>{formatMins(totalMins)} estimated</span>
                        {(planResult?.preferenceConflicts?.length || 0) > 0 && (
                            <span className="text-amber-600 flex items-center gap-1"><AlertTriangle size={11} /> {planResult.preferenceConflicts.length} preference {planResult.preferenceConflicts.length === 1 ? 'conflict' : 'conflicts'}</span>
                        )}
                        {unscheduled.length > 0 && (
                            <span className="text-red-500 flex items-center gap-1"><AlertTriangle size={11} /> {unscheduled.length} unscheduled</span>
                        )}
                    </div>

                    {/* Crew legend */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {selectedGroups.map((g, idx) => (
                            <div key={g.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-white border rounded-full text-xs font-medium text-gray-700">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CREW_COLORS[idx % CREW_COLORS.length] }} />
                                {g.name}
                            </div>
                        ))}
                        {!isMobile && (
                            <div className="flex items-center gap-1 text-xs text-gray-400 ml-1">
                                <GripVertical size={11} /> Drag to reassign day or crew
                            </div>
                        )}
                        {isMobile && (
                            <div className="flex items-center gap-1 text-xs text-gray-400 ml-1">
                                <ArrowRightLeft size={11} /> Tap ⇄ to move between days or crews
                            </div>
                        )}
                    </div>

                    {/* Conflicts */}
                    {planResult?.preferenceConflicts?.length > 0 && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle size={12} /> Preference conflicts</p>
                            {planResult.preferenceConflicts.slice(0, 5).map((c, i) => (
                                <p key={i} className="text-xs text-amber-700"><strong>{c.customerName}</strong> — prefers {capitalize(c.preferredDay)}, placed on {capitalize(c.assignedDay)} ({c.reason})</p>
                            ))}
                            {planResult.preferenceConflicts.length > 5 && <p className="text-xs text-amber-600 mt-1">+{planResult.preferenceConflicts.length - 5} more</p>}
                        </div>
                    )}

                    {/* THE GRID */}
                    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                        <div style={{ minWidth: `${140 + selectedGroups.length * 200}px` }}>
                            <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: `140px repeat(${selectedGroups.length}, 1fr)` }}>
                                <div />
                                {selectedGroups.map((g, idx) => (
                                    <div key={g.id} className="text-center">
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: CREW_COLORS[idx % CREW_COLORS.length] }}>
                                            {g.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {editableDays.map(day => (
                                <div key={day.date} className="grid gap-2 mb-3" style={{ gridTemplateColumns: `140px repeat(${selectedGroups.length}, 1fr)` }}>
                                    <div className="flex flex-col justify-center pr-2">
                                        <p className="text-sm font-bold text-gray-800 capitalize">{day.dayName}</p>
                                        <p className="text-xs text-gray-400">{format(parseISO(day.date), 'MMM d')}</p>
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                            {Object.values(day.crews).reduce((s, c) => s + c.customers.length, 0)} total
                                        </p>
                                        {(() => {
                                            const dayName = day.dayName.toLowerCase();
                                            const count = Object.values(day.crews).flatMap(c => c.customers)
                                                .filter(c => c.preferredDays?.includes(dayName)).length;
                                            return count > 0 ? (
                                                <span className="text-[10px] text-blue-500 flex items-center gap-0.5 mt-0.5">
                                                    <Lock size={9} /> {count} prefer {day.dayName.slice(0,3)}
                                                </span>
                                            ) : null;
                                        })()}
                                    </div>
                                    {selectedGroups.map((g, idx) => {
                                        const isDragOver = dragOverCell?.date === day.date && dragOverCell?.crewId === g.id;
                                        return (
                                            <CrewDayCell
                                                key={g.id}
                                                crewData={day.crews[g.id] || { crewId: g.id, crewName: g.name, customers: [], stats: {} }}
                                                crewColor={CREW_COLORS[idx % CREW_COLORS.length]}
                                                crewName={g.name}
                                                isDragOver={isDragOver}
                                                isMobile={isMobile}
                                                onDragStart={(c) => handleDragStart(c, day.date, g.id)}
                                                onDrop={() => handleDrop(day.date, g.id)}
                                                onDragOver={() => setDragOverCell({ date: day.date, crewId: g.id })}
                                                onDragLeave={() => setDragOverCell(null)}
                                                onMoveCustomer={(c, fromIdx, toIdx) => handleMoveCustomer(day.date, g.id, c, fromIdx, toIdx)}
                                                onOpenMoveSheet={(c) => openMoveSheet(c, day.date, g.id)}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>

                    {unscheduled.length > 0 && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1"><AlertTriangle size={13} /> No valid address — couldn't schedule</p>
                            <div className="flex flex-wrap gap-2">
                                {unscheduled.map(c => <span key={c.id} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-lg">{c.name}</span>)}
                            </div>
                        </div>
                    )}

                    {/* Preferred crew not selected warning */}
                    {(() => {
                        const mismatched = customers.filter(c =>
                            selectedCustomerIds.includes(c.id) &&
                            c.preferredCrewId &&
                            !selectedGroupIds.includes(c.preferredCrewId)
                        );
                        if (mismatched.length === 0) return null;
                        return (
                            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                                    <AlertTriangle size={13} /> Preferred crew not included in this plan
                                </p>
                                <p className="text-xs text-amber-700 mb-2">
                                    The following customers have a preferred crew that wasn't selected. They've been assigned automatically by geography — drag them to the correct crew if needed, or go back and add their preferred crew.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {mismatched.map(c => {
                                        const pref = routeGroups.find(g => g.id === c.preferredCrewId);
                                        return (
                                            <span key={c.id} className="text-xs bg-amber-100 text-amber-800 px-2.5 py-1 rounded-lg border border-amber-200 font-medium">
                                                {c.name} <span className="font-normal opacity-75">→ prefers {pref?.name || 'unknown crew'}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}

                    <div className="flex gap-3 mt-6">
                        <button onClick={() => setStep(1)} className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 text-sm">
                            <ChevronLeft size={16} /> Back
                        </button>
                        <button onClick={() => setStep(3)} disabled={totalJobs === 0}
                            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold text-sm hover:bg-blue-700 disabled:opacity-50 shadow-sm">
                            Looks Good <ChevronRight size={16} />
                        </button>
                        <button onClick={handleRunPlan} disabled={isPlanning}
                            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 ml-auto">
                            <RotateCcw size={14} className={isPlanning ? 'animate-spin' : ''} /> Rebalance
                        </button>
                    </div>
                </div>
            )}

            {/* ── STEP 3 — CONFIRM ──────────────────────────────────── */}
            {step === 3 && (
                <div className="max-w-2xl space-y-5">
                    <h2 className="text-lg font-bold text-gray-800">Confirm Schedule</h2>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b grid grid-cols-5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <span>Day</span><span>Crew</span><span className="text-center">Jobs</span><span className="text-center">Service</span><span className="text-center">Drive</span>
                        </div>
                        {editableDays.map(day =>
                            selectedGroups.map((g, gIdx) => {
                                const cd = day.crews[g.id];
                                if (!cd || cd.customers.length === 0) return null;
                                return (
                                    <div key={`${day.date}-${g.id}`} className="grid grid-cols-5 px-4 py-2.5 border-b last:border-0 items-center">
                                        <p className="text-sm text-gray-700 capitalize">{gIdx === 0 ? day.dayName : ''}</p>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CREW_COLORS[gIdx % CREW_COLORS.length] }} />
                                            <p className="text-xs text-gray-600 truncate">{g.name}</p>
                                        </div>
                                        <p className="text-sm text-center font-semibold text-gray-700">{cd.customers.length}</p>
                                        <p className="text-sm text-center text-gray-600">{formatMins(cd.stats?.totalServiceMinutes)}</p>
                                        <p className="text-sm text-center text-gray-600">{formatMins(cd.stats?.driveMinutes)}</p>
                                    </div>
                                );
                            })
                        )}
                        <div className="grid grid-cols-5 px-4 py-3 bg-gray-50 border-t">
                            <p className="text-xs font-bold text-gray-600 col-span-2">Total</p>
                            <p className="text-xs font-bold text-center text-gray-700">{totalJobs}</p>
                            <p className="text-xs font-bold text-center text-gray-700">{formatMins(totalMins - totalDriveMins)}</p>
                            <p className="text-xs font-bold text-center text-gray-700">{formatMins(totalDriveMins)}</p>
                        </div>
                    </div>

                    <div className={`p-4 rounded-xl border transition-all ${sendEmails ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {sendEmails ? <Mail size={16} className="text-blue-600" /> : <MailX size={16} className="text-gray-400" />}
                                <span className={`text-sm font-semibold ${sendEmails ? 'text-blue-800' : 'text-gray-500'}`}>Booking confirmation emails</span>
                            </div>
                            <button type="button" onClick={() => setSendEmails(p => !p)}
                                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${sendEmails ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${sendEmails ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                        </div>
                        {sendEmails && <p className="text-xs text-blue-700 mt-2">Each customer receives a confirmation with their date and arrival window.</p>}
                    </div>

                    {(isCreating || isOptimizingRoutes) && (
                        <div className="p-4 bg-white border rounded-xl space-y-3">
                            <div>
                                <div className="flex items-center justify-between text-sm mb-1.5">
                                    <span className={`font-medium flex items-center gap-1.5 ${!isCreating ? 'text-green-600' : 'text-gray-700'}`}>
                                        {!isCreating && <CheckCircle size={13} />}
                                        Creating jobs
                                    </span>
                                    <span className="text-gray-500 text-xs">{createProgress} / {createTotal}</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full transition-all ${!isCreating ? 'bg-green-500' : 'bg-blue-600'}`}
                                        style={{ width: `${createTotal > 0 ? (createProgress / createTotal) * 100 : 0}%` }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between text-sm mb-1.5">
                                    <span className={`font-medium flex items-center gap-1.5 ${isOptimizingRoutes ? 'text-gray-700' : isCreating ? 'text-gray-400' : 'text-green-600'}`}>
                                        {!isCreating && !isOptimizingRoutes && <CheckCircle size={13} />}
                                        {isOptimizingRoutes && <Loader2 size={13} className="animate-spin" />}
                                        Optimizing routes
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                        {isOptimizingRoutes ? 'Calculating best stop order…' : isCreating ? 'Up next' : 'Done'}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full transition-all ${isOptimizingRoutes ? 'bg-blue-600 animate-pulse' : isCreating ? 'bg-gray-200' : 'bg-green-500'}`}
                                        style={{ width: isCreating ? '0%' : isOptimizingRoutes ? '60%' : '100%' }} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button onClick={() => setStep(2)} disabled={isCreating}
                            className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 text-sm disabled:opacity-50">
                            <ChevronLeft size={16} /> Back
                        </button>
                        {isReopen ? (
                            <button onClick={handleSaveReopen} disabled={isSavingReopen || isOptimizingRoutes || totalJobs === 0}
                                className="flex items-center gap-2 px-8 py-2.5 bg-amber-500 text-white rounded-lg font-bold text-sm hover:bg-amber-600 disabled:opacity-50 shadow-sm">
                                {isSavingReopen ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><CheckCircle size={16} /> Save Changes</>}
                            </button>
                        ) : (
                            <button onClick={handleCreateJobs} disabled={isCreating || isOptimizingRoutes || totalJobs === 0}
                                className="flex items-center gap-2 px-8 py-2.5 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700 disabled:opacity-50 shadow-sm">
                                {isCreating ? <><Loader2 size={16} className="animate-spin" /> Creating…</> : <><CheckCircle size={16} /> Create {totalJobs} Jobs</>}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ── CREW OVERRIDE CONFIRMATION MODAL ─────────────────── */}
            {/* ── MOBILE MOVE ACTION SHEET ────────────────────────── */}
            {moveSheet && (() => {
                const { customer, fromDate, fromCrewId } = moveSheet;
                return (
                    <>
                        <div className="fixed inset-0 z-40 bg-black/30" onClick={closeMoveSheet} />
                        <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[75vh] overflow-y-auto">
                            <div className="flex justify-center pt-3 pb-1">
                                <div className="w-10 h-1 bg-gray-300 rounded-full" />
                            </div>

                            <div className="px-4 pb-2 pt-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Move {customer.name}</h3>
                                        <p className="text-xs text-gray-400 mt-0.5">Select a new day or crew below</p>
                                    </div>
                                    <button onClick={closeMoveSheet} className="p-2 rounded-full hover:bg-gray-100">
                                        <X size={18} className="text-gray-400" />
                                    </button>
                                </div>

                                <div className="mb-5">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Change Day</p>
                                    <div className="flex flex-wrap gap-2">
                                        {editableDays.map(day => {
                                            const isCurrent = day.date === fromDate;
                                            const totalStops = Object.values(day.crews).reduce((s, c) => s + c.customers.length, 0);
                                            const dayName = day.dayName;
                                            const isPreferred = customer.preferredDays?.includes(dayName.toLowerCase());
                                            return (
                                                <button
                                                    key={day.date}
                                                    type="button"
                                                    disabled={isCurrent}
                                                    onClick={() => executeMoveFromSheet(day.date, fromCrewId)}
                                                    className={`flex flex-col items-center px-3 py-2 rounded-xl border text-sm font-semibold transition-all ${
                                                        isCurrent
                                                            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-default'
                                                            : isPreferred
                                                                ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                                                                : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                                                    }`}
                                                >
                                                    <span className="capitalize">{dayName.slice(0, 3)}</span>
                                                    <span className="text-[10px] font-normal mt-0.5 opacity-70">{totalStops} stops</span>
                                                    {isPreferred && <span className="text-[9px] text-blue-500 mt-0.5">preferred</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {selectedGroups.length > 1 && (
                                    <div className="mb-5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Change Crew</p>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedGroups.map((g, gIdx) => {
                                                const isCurrent = g.id === fromCrewId;
                                                const color = CREW_COLORS[gIdx % CREW_COLORS.length];
                                                const isPreferredCrew = customer.preferredCrewId === g.id;
                                                const stopsOnDay = (editableDays.find(d => d.date === fromDate)?.crews[g.id]?.customers || []).length;
                                                return (
                                                    <button
                                                        key={g.id}
                                                        type="button"
                                                        disabled={isCurrent}
                                                        onClick={() => executeMoveFromSheet(fromDate, g.id)}
                                                        className={`flex flex-col items-center px-3 py-2 rounded-xl border text-sm font-semibold transition-all ${
                                                            isCurrent
                                                                ? 'border-gray-200 text-gray-400 cursor-default'
                                                                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                                        }`}
                                                        style={!isCurrent ? { borderColor: color + '66', backgroundColor: color + '11' } : {}}
                                                    >
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: isCurrent ? '#d1d5db' : color }} />
                                                            <span style={{ color: isCurrent ? '#9ca3af' : color }}>{g.name}</span>
                                                        </div>
                                                        <span className="text-[10px] font-normal mt-0.5 opacity-70">{stopsOnDay} stops today</span>
                                                        {isPreferredCrew && <span className="text-[9px] mt-0.5" style={{ color }}>preferred</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {selectedGroups.length > 1 && (
                                    <div className="mb-5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Move to Different Day + Crew</p>
                                        <div className="space-y-2">
                                            {editableDays.map(day => {
                                                const isSameDay = day.date === fromDate;
                                                return selectedGroups.map((g, gIdx) => {
                                                    if (isSameDay && g.id === fromCrewId) return null;
                                                    const color = CREW_COLORS[gIdx % CREW_COLORS.length];
                                                    const stops = (day.crews[g.id]?.customers || []).length;
                                                    return (
                                                        <button key={`${day.date}-${g.id}`} type="button"
                                                            onClick={() => executeMoveFromSheet(day.date, g.id)}
                                                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                                                <span className="text-sm font-medium text-gray-800 capitalize">{day.dayName}</span>
                                                                <span className="text-xs text-gray-400">· {g.name}</span>
                                                            </div>
                                                            <span className="text-xs text-gray-400">{stops} stops</span>
                                                        </button>
                                                    );
                                                });
                                            })}
                                        </div>
                                    </div>
                                )}

                                <button onClick={closeMoveSheet}
                                    className="w-full py-3 border border-gray-200 text-gray-500 rounded-xl font-medium text-sm hover:bg-gray-50 mb-4">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </>
                );
            })()}

            {/* ── DAY PREFERENCE OVERRIDE CONFIRMATION ────────────── */}
            {pendingDayOverride && (() => {
                const { customer, toDate, fromDate } = pendingDayOverride;
                const toDateDayName = new Date(toDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
                const fromDateDayName = new Date(fromDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
                const preferredDayNames = (customer.preferredDays || []).map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ');
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={cancelDayOverride} />
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <CalendarDays size={20} className="text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-base">Move off preferred day?</h3>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        <strong>{customer.name}</strong> prefers <strong>{preferredDayNames}</strong>.
                                    </p>
                                </div>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5 text-xs text-blue-800">
                                You're moving them from <strong>{fromDateDayName}</strong> to <strong>{toDateDayName}</strong>. This only affects this week's plan — their preference on the customer record is unchanged.
                            </div>
                            <div className="flex gap-3">
                                <button onClick={cancelDayOverride}
                                    className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors">
                                    Keep on {fromDateDayName}
                                </button>
                                <button onClick={confirmDayOverride}
                                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors">
                                    Move anyway
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {pendingCrewOverride && (() => {
                const { customer, toCrewId, fromCrewId } = pendingCrewOverride;
                const preferredCrew = routeGroups.find(g => g.id === customer.preferredCrewId);
                const targetCrew    = routeGroups.find(g => g.id === toCrewId);
                const sourceCrew    = routeGroups.find(g => g.id === fromCrewId);
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={cancelCrewOverride} />
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <Lock size={20} className="text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-base">Override preferred crew?</h3>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        <strong>{customer.name}</strong> is set to prefer <strong>{preferredCrew?.name || 'their assigned crew'}</strong>.
                                    </p>
                                </div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-xs text-amber-800">
                                You're moving them from <strong>{sourceCrew?.name || fromCrewId}</strong> to <strong>{targetCrew?.name || toCrewId}</strong>. This only affects this week's plan — their preference on the customer record is unchanged.
                            </div>
                            <div className="flex gap-3">
                                <button onClick={cancelCrewOverride}
                                    className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors">
                                    Keep in {preferredCrew?.name || 'preferred crew'}
                                </button>
                                <button onClick={confirmCrewOverride}
                                    className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors">
                                    Move anyway
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default WeeklyPlannerView;